import { send } from '@emailjs/browser';
import emailjsConfig from './emailjsConfig';

/**
 * Checks if EmailJS is properly configured
 * @returns {Object} - Object with status and any configuration errors
 */
const checkEmailJSConfiguration = () => {
  const issues = [];
  
  if (!emailjsConfig.serviceId) {
    issues.push('EmailJS Service ID is missing');
  }
  
  if (!emailjsConfig.templateId) {
    issues.push('EmailJS Template ID is missing');
  }
  
  if (!emailjsConfig.publicKey) {
    issues.push('EmailJS Public Key is missing');
  }
  
  return {
    isConfigured: issues.length === 0,
    issues
  };
};

/**
 * Sends a booking confirmation email to the customer
 * @param {Object} booking - The booking object with all details
 * @returns {Promise} - Promise resolving to the EmailJS response
 */
export const sendBookingConfirmationEmail = async (booking) => {
  if (!booking) {
    throw new Error('Booking data is required to send confirmation email');
  }

  if (!booking.customerEmail) {
    throw new Error('Customer email is required for sending confirmation email');
  }

  const customerEmail = booking.customerEmail.trim();
  if (!customerEmail) {
    throw new Error('Customer email cannot be empty');
  }

  // Log the email being used
  console.log('Sending confirmation email to:', customerEmail);

  // Check EmailJS configuration first
  const config = checkEmailJSConfiguration();
  if (!config.isConfigured) {
    console.error('EmailJS configuration issues:', config.issues);
    throw new Error(`EmailJS configuration error: ${config.issues.join(', ')}`);
  }

  // Format price strings
  const formatPrice = (price) => {
    return typeof price === 'number' 
      ? `₹${price.toLocaleString('en-IN')}` 
      : price;
  };

  // Prepare add-ons string if any
  let addOnsText = '';
  if (booking.addOns && booking.addOns.length > 0) {
    addOnsText = booking.addOns.map(addon => 
      `${addon.name} - ${formatPrice(addon.price)}`
    ).join('<br>');
  }

  // Prepare coupon info if any
  let couponText = '';
  if (booking.couponCode) {
    const discountType = booking.couponType === 'percentage' 
      ? `${booking.couponValue}%` 
      : formatPrice(booking.couponValue);
    
    couponText = `
      Applied Coupon: ${booking.couponCode}<br>
      Discount: ${discountType} off<br>
      Amount Saved: ${formatPrice(booking.couponDiscount)}
    `;
  }

  // Create the exact parameters as shown in the template
  const templateParams = {
    to_name: booking.customerName || 'Customer',
    to_email: customerEmail, // Required for recipient
    booking_id: booking.id || 'N/A',
    service_name: booking.serviceName || 'N/A',
    booking_date: booking.bookingDate || 'N/A',
    booking_time: booking.bookingTime || 'N/A',
    location: booking.cityName ? `${booking.cityName}, Pincode: ${booking.pincode || ''}` : `Pincode: ${booking.pincode || ''}`,
    contact_phone: booking.customerPhone || 'N/A',
    contact_email: customerEmail,
    special_requests: booking.specialRequests || 'None',
    flex_backdrop_text: booking.flexBackdropText || 'None',
    base_price: formatPrice(booking.basePrice || booking.servicePrice || 0),
    addons_price: formatPrice(booking.addOnsPrice || 0),
    addons_list: addOnsText || 'None',
    subtotal: formatPrice(booking.adjustedPrice || 0),
    coupon_info: couponText || 'None',     // <-- Add fallback
    discount_amount: booking.couponDiscount ? formatPrice(booking.couponDiscount) : '₹0',
    final_amount: formatPrice(booking.finalPrice || booking.adjustedPrice || 0),
    payment_status: booking.paymentStatus || 'Pending',
    reply_to: customerEmail
};

  // Log the params we're sending
  console.log('Sending email with parameters:', templateParams);

  // Using hardcoded values from emailjsConfig for maximum reliability
  try {
    const response = await send(
      "service_4z218de",
      "template_1gc3heb",
      templateParams,
      "8jAv2xPjMlzbVElUz"
    );
    
    console.log('Email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('Error sending confirmation email. Details:', error);
    console.error('Error status:', error.status);
    console.error('Error text:', error.text);
    throw error;
  }
};

/**
 * Sends a custom email using the booking confirmation template
 * @param {Object} params - Custom parameters for the email template
 * @returns {Promise} - Promise resolving to the EmailJS response
 */
export const sendCustomEmail = async (params) => {
  if (!params || !params.to_email || !params.to_name) {
    throw new Error('Email recipient information is required');
  }
  
  const cleanEmail = params.to_email.trim();
  if (!cleanEmail) {
    throw new Error('Recipient email cannot be empty');
  }
  
  // Ensure to_email is properly set
  const templateParams = {
    ...params,
    to_email: cleanEmail
  };
  
  console.log('Sending custom email to:', cleanEmail);
  
  try {
    // Using hardcoded credentials for maximum reliability
    const response = await send(
      "service_4z218de",
      "template_1gc3heb",
      templateParams,
      "8jAv2xPjMlzbVElUz"
    );
    
    console.log('Custom email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('Error sending custom email. Details:', error);
    console.error('Error status:', error.status);
    console.error('Error text:', error.text);
    throw error;
  }
};

/**
 * Sends a test email to verify EmailJS configuration
 * @param {string} toEmail - Email address to send the test to
 * @returns {Promise} - Promise resolving to the EmailJS response
 */
export const sendTestEmail = async (toEmail) => {
  if (!toEmail) {
    throw new Error('Recipient email is required for test email');
  }
  
  const cleanEmail = toEmail.trim();
  if (!cleanEmail) {
    throw new Error('Recipient email cannot be empty');
  }
  
  console.log('Sending test email to:', cleanEmail);
  
  // Simplified parameters for test email - match the template exactly
  const testParams = {
    to_email: cleanEmail,
    to_name: 'Test User',
    booking_id: 'TEST-123',
    service_name: 'Test Service',
    booking_date: new Date().toLocaleDateString(),
    booking_time: '10:00 AM to 1:00 PM',
    location: 'Test Location, Pincode: 123456',
    contact_phone: '9876543210',
    contact_email: cleanEmail,
    special_requests: 'This is a test email',
    flex_backdrop_text: 'TEST BACKDROP',
    base_price: '₹1,000',
    addons_price: '₹0',
    addons_list: 'None',
    subtotal: '₹1,000',
    coupon_info: '',
    discount_amount: '₹0',
    final_amount: '₹1,000',
    payment_status: 'Test'
  };
  
  console.log('Sending test email with params:', testParams);
  
  try {
    // Using hardcoded credentials for maximum reliability
    const response = await send(
      "service_4z218de",
      "template_1gc3heb",
      testParams,
      "8jAv2xPjMlzbVElUz"
    );
    
    console.log('Test email sent successfully:', response);
    return {
      success: true,
      message: 'Test email sent successfully',
      response
    };
  } catch (error) {
    console.error('Error sending test email. Details:', error);
    console.error('Error status:', error.status);
    console.error('Error text:', error.text);
    
    return {
      success: false,
      message: `Failed to send test email: ${error.text || error.message}`,
      error
    };
  }
};

/**
 * Default export for backward compatibility
 */
export default {
  sendBookingConfirmationEmail,
  sendCustomEmail,
  sendTestEmail
}; 